import {useCallback, useEffect, useRef} from 'react';
import {getAllFocusableElements, resetFocusToRoot} from './helpers';
import {FocusManagementType} from './types';

/**
 * Implements focus trapping behavior for popovers, and other overlay components.
 * It ensures that keyboard focus remains within a specific container when it's active,
 * improving accessibility for keyboard users. This hook is mostly for internal use
 * in this package, prefer using `useFocusManagement` since it ensures that an opened
 * popover is added to the stack of opened popovers, and prevents adding the keyboard
 * listeners to closed popovers.
 *
 * @param param0
 */
export const useFocusTrap = ({
  isOpen,
  containerRef,
  disable,
  openerElement,
}: FocusManagementType) => {
  const shouldFocusOpenerOnClose = useRef(false);
  // A popover that is covered by another one, e.g. the size selection under
  // the size reminder, must not pull focus out of the top most popover when it closes.
  const isCoveredRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      isCoveredRef.current = !!disable;

      if (disable) {
        return;
      }

      const container = containerRef?.current;

      if (!container || shouldFocusOpenerOnClose.current) {
        return;
      }

      const focusableElements = getAllFocusableElements(container);

      if (focusableElements.length === 0) {
        return;
      }

      focusableElements[0]?.focus();
      shouldFocusOpenerOnClose.current = true;
    } else {
      if (
        shouldFocusOpenerOnClose.current &&
        !isCoveredRef.current &&
        openerElement
      ) {
        // `body` isn't naturally focusable, so a plain `focus()` call on it
        // is a no-op — it needs the same reset as having no opener at all.
        if (openerElement === document.body) {
          resetFocusToRoot();
        } else {
          openerElement.focus({preventScroll: true});
        }
      }
    }
  }, [isOpen, disable, containerRef.current]);

  const tabNavigation = useCallback(
    (e: KeyboardEvent) => {
      if (disable) {
        return null;
      }

      shouldFocusOpenerOnClose.current = true;

      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

      if (!isTabPressed) {
        return null;
      }

      const container = containerRef.current;

      if (!container) {
        return null;
      }

      const focusableElements = getAllFocusableElements(container);

      if (focusableElements.length === 0) {
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      if (!container?.contains(document.activeElement)) {
        e.preventDefault();
        firstFocusableElement?.focus();
      } else if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          e.preventDefault();
          lastFocusableElement?.focus();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          e.preventDefault();
          firstFocusableElement?.focus();
        }
      }
    },
    [containerRef.current, disable],
  );

  useEffect(() => {
    if (isOpen && !disable) {
      document.addEventListener('keydown', tabNavigation);
    }

    return () => {
      document.removeEventListener('keydown', tabNavigation);
    };
  }, [isOpen, disable, containerRef.current]);
};
